import React, { useState } from 'react'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@material-ui/core'
import HelpIcon from '@material-ui/icons/Help'
import ScheduleIcon from '@material-ui/icons/Schedule'
import { ezScheduleDetails } from '../../../../../models/WorkOrder/types'
import { Container } from '../../../../../components'
import { useScheduleReviewStyles } from '../styles'
import { ErrorPanel } from './ErrorPanel'

interface Props {
    ezSchedule: ezScheduleDetails
    templateIsValid: boolean
    onOpenTurnReview: () => void
    generateTemplateWorkorders: () => void
    generateTemplateWorkordersLoading: boolean
    templateWorkordersLoading: boolean
    selectedPhaseId?: number
}

interface HelpStep {
    id: string
    title: string
    subtitle: string
    items: string[]
    accentColor: string
    surfaceColor: string
    borderColor: string
}

export const ReviewHeader = (props: Props) => {
    const {
        ezSchedule,
        templateIsValid,
        onOpenTurnReview,
        generateTemplateWorkorders,
        generateTemplateWorkordersLoading,
        templateWorkordersLoading,
        selectedPhaseId,
    } = props
    const classes = useScheduleReviewStyles()
    const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false)
    const isGenerating =
        generateTemplateWorkordersLoading ||
        (templateIsValid && templateWorkordersLoading)

    const selectedPhase =
        ezSchedule.phases.find(
            (phase) => phase.phase?.id === selectedPhaseId,
        ) ?? ezSchedule.phases[0]
    const templateServices = selectedPhase?.template_services ?? []
    const totalServiceAreas = selectedPhase?.total_service_areas ?? 0
    const hasNoServiceAreas = totalServiceAreas === 0

    const helpSteps: HelpStep[] = [
        {
            id: 'setup',
            title: '1. Setup',
            subtitle: 'Adjust your schedule details before running automation.',
            items: [
                'Use the arrows on the Vendors bars to adjust their work start and end dates.',
                'For services with multiple Vendors, you may optionally assign units for each Vendor by clicking Assign Units.',
                'To modify transfer dates, destination or double-transfer ready dates, or schedule days off, click the dates directly in the chart.',
            ],
            accentColor: '#1E40AF',
            surfaceColor: '#EFF6FF',
            borderColor: '#BFDBFE',
        },
        {
            id: 'generate-template-workorders',
            title: '2. Generate Template Workorders',
            subtitle: 'Create draft workorders based on your plan.',
            items: [
                'When you are finished with your changes, click Run EZ Scheduler to generate template workorders.',
            ],
            accentColor: '#0369A1',
            surfaceColor: '#F0F9FF',
            borderColor: '#BAE6FD',
        },
        {
            id: 'review-and-schedule',
            title: '3. Review and Schedule',
            subtitle:
                'Review the results of your planned project and schedule them on your turn board.',
            items: [
                'Click Review & Schedule to review and schedule the generated workorders.',
            ],
            accentColor: '#0F766E',
            surfaceColor: '#F0FDFA',
            borderColor: '#99F6E4',
        },
    ]

    return (
        <Container className={classes.header}>
            <Container className={classes.headerLeft}>
                <Button
                    variant="outlined"
                    color="primary"
                    className={classes.helpButton}
                    startIcon={<HelpIcon />}
                    onClick={() => setHelpDialogOpen(true)}
                >
                    Help
                </Button>
            </Container>
            <Container className={classes.headerCenter}>
                <Container className={classes.infoPanel}>
                    <ErrorPanel
                        ezSchedule={ezSchedule}
                        templateServices={templateServices}
                        selectedPhaseId={selectedPhaseId}
                    />
                </Container>
            </Container>
            <Container className={classes.headerRight}>
                {templateIsValid &&
                !templateWorkordersLoading &&
                !hasNoServiceAreas ? (
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.primaryCtaButton}
                        onClick={onOpenTurnReview}
                        startIcon={<ScheduleIcon />}
                    >
                        Review & Schedule
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        className={`${classes.primaryCtaButton} ${
                            isGenerating ? classes.primaryCtaButtonLoading : ''
                        }`}
                        disabled={
                            generateTemplateWorkordersLoading ||
                            templateIsValid ||
                            hasNoServiceAreas
                        }
                        onClick={() => {
                            generateTemplateWorkorders()
                        }}
                        startIcon={
                            isGenerating ? (
                                <CircularProgress
                                    size={18}
                                    className={classes.primaryCtaSpinner}
                                />
                            ) : (
                                <PlayArrowIcon />
                            )
                        }
                    >
                        Run EZ Scheduler
                    </Button>
                )}
            </Container>
            {/* Help Dialog */}
            <Dialog
                open={helpDialogOpen}
                onClose={() => setHelpDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Review Help
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{ color: '#4B5563', marginTop: 4 }}
                    >
                        Follow these steps to create and schedule your template
                        workorders.
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            paddingTop: 8,
                        }}
                    >
                        {helpSteps.map((step) => (
                            <Box
                                key={step.id}
                                style={{
                                    border: `1px solid ${step.borderColor}`,
                                    borderLeft: `5px solid ${step.accentColor}`,
                                    borderRadius: 10,
                                    backgroundColor: step.surfaceColor,
                                    padding: '12px 16px',
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        color: step.accentColor,
                                        fontWeight: 700,
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: '#374151',
                                        marginTop: 2,
                                        marginBottom: 8,
                                    }}
                                >
                                    {step.subtitle}
                                </Typography>
                                <ul
                                    style={{
                                        margin: 0,
                                        paddingLeft: 20,
                                        display: 'grid',
                                        gap: 6,
                                        color: '#111827',
                                    }}
                                >
                                    {step.items.map((item) => (
                                        <li
                                            key={`${step.id}-${item}`}
                                            style={{ whiteSpace: 'normal' }}
                                        >
                                            <Typography variant="body2">
                                                {item}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setHelpDialogOpen(false)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
